package com.lightningkite.kiteui.reactive

import kotlin.properties.ReadOnlyProperty
import kotlin.properties.ReadWriteProperty
import kotlin.reflect.KProperty

interface ResourceUse {
    fun beginUse(): () -> Unit
}

interface Listenable : ResourceUse {
    /**
     * Adds the [listener] to be called every time this event fires.
     * @return a function to remove the [listener] that was added.  Removing multiple times should not cause issues.
     */
    fun addListener(listener: () -> Unit): () -> Unit
    override fun beginUse(): () -> Unit = addListener { }

    object Never: Listenable {
        override fun addListener(listener: () -> Unit): () -> Unit = {}
    }
}
fun Listenable.addAndRunListener(listener: () -> Unit): () -> Unit {
    val remover = addListener(listener)
    listener()
    return remover
}

interface Readable<out T> : Listenable {
    val state: ReadableState<T>
    object Never: Readable<Nothing> {
        override val state: ReadableState<Nothing> get() = ReadableState.notReady
        override fun addListener(listener: () -> Unit): () -> Unit = {}
    }
}

interface WriteOnly<T> {
    suspend infix fun set(value: T)
}

interface Writable<T> : Readable<T>, WriteOnly<T>

interface ImmediateReadable<out T> : Readable<T>, ReadOnlyProperty<Any?, T> {
    val value: T
    override val state: ReadableState<T> get() = ReadableState(value)
    override fun getValue(thisRef: Any?, property: KProperty<*>): T = value
}

interface ImmediateWriteOnly<T>: WriteOnly<T> {
    fun setImmediate(value: T)
    override suspend fun set(value: T) { setImmediate(value) }
}

interface ImmediateReadableWithWrite<T> : Writable<T>, ImmediateReadable<T>
interface ReadableWithImmediateWrite<T> : Writable<T>, ImmediateWriteOnly<T>

interface ImmediateWritable<T> : ImmediateWriteOnly<T>, ImmediateReadable<T>, Writable<T>, ReadWriteProperty<Any?, T> {
    override var value: T
    override fun setImmediate(value: T) { this.value = value }
    override suspend fun set(value: T) { this.value = value }
    override fun getValue(thisRef: Any?, property: KProperty<*>): T = value
    override fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
        this.value = value
    }
}

class NotReadyException(message: String? = null) : IllegalStateException(message)

